import React, { useMemo } from 'react';
import { Slide, Theme, SlideElement, AspectRatio, ThemeFont, SlideBorderStyle, GridLayoutItem } from '../types';
import { SlideBackground } from './SlideBackground';
import { SlidePatternOverlay } from './SlidePatternOverlay';
import { SlideElements } from './SlideElements';

interface SlideRendererProps {
  slide: Slide;
  theme: Theme;
  aspectRatio: AspectRatio;
  language: 'ko' | 'en';
  themeFont?: ThemeFont;
  borderStyle?: SlideBorderStyle;
  isEditable?: boolean;
  scale?: number;
  containerWidth?: number;
  containerHeight?: number;
  disableTransformScale?: boolean;
  onElementSelect?: (elementId: string, event?: MouseEvent | React.MouseEvent) => void;
  selectedElementId?: string;
  selectedElementIds?: string[];
  editingElementId?: string | null;
  onElementUpdate?: (elementId: string, updates: Partial<SlideElement>) => void;
  onMultiElementUpdate?: (updates: Array<{id: string, updates: Partial<SlideElement>}>) => void;
  onElementDelete?: (elementId: string) => void;
  onEditingElementChange?: (elementId: string | null) => void;
  onSlideUpdate?: (updates: Partial<Slide>) => void;
  onLayoutChange?: (layout: GridLayoutItem[]) => void;
  isDarkMode?: boolean;
}

export const SlideRenderer: React.FC<SlideRendererProps> = React.memo(({
  slide,
  theme,
  aspectRatio,
  language,
  themeFont,
  borderStyle,
  isEditable = false,
  scale = 1,
  containerWidth,
  containerHeight,
  disableTransformScale = false,
  onElementSelect,
  selectedElementId,
  selectedElementIds,
  editingElementId,
  onElementUpdate,
  onMultiElementUpdate,
  onElementDelete,
  onEditingElementChange,
  onSlideUpdate,
  onLayoutChange,
  isDarkMode = false,
}) => {
  // 실제 렌더링될 크기 계산 - 종횡비 기반으로 계산
  const aspectRatioValue = aspectRatio.width / aspectRatio.height;
  
  // 컨테이너 크기가 주어진 경우 그것을 사용하고, 아니면 종횡비 기반으로 계산
  let renderWidth, renderHeight;
  
  if (containerWidth && containerHeight) {
    // 컨테이너 크기가 지정된 경우, 올바른 종횡비로 조정
    const containerAspectRatio = containerWidth / containerHeight;
    
    if (Math.abs(containerAspectRatio - aspectRatioValue) < 0.01) {
      // 종횡비가 거의 일치하는 경우 컨테이너 크기 그대로 사용
      renderWidth = containerWidth;
      renderHeight = containerHeight;
    } else if (containerAspectRatio > aspectRatioValue) {
      // 컨테이너가 더 넓은 경우, 높이를 기준으로 조정
      renderHeight = containerHeight;
      renderWidth = renderHeight * aspectRatioValue;
    } else {
      // 컨테이너가 더 좁은 경우, 너비를 기준으로 조정
      renderWidth = containerWidth;
      renderHeight = renderWidth / aspectRatioValue;
    }
  } else {
    // 컨테이너 크기가 지정되지 않은 경우, 기본 종횡비 크기 사용
    renderWidth = aspectRatio.width;
    renderHeight = aspectRatio.height;
  }
  
  const actualWidth = renderWidth;
  const actualHeight = renderHeight;

  // 그림자 타입에 따른 박스섀도우 계산
  const shadowStyle = useMemo(() => {
    if (!borderStyle?.shadowType || borderStyle.shadowType === 'none') {
      return borderStyle?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)';
    }
    
    switch (borderStyle.shadowType) {
      case 'light':
        return '0 2px 8px rgba(0,0,0,0.08)';
      case 'medium':
        return '0 4px 16px rgba(0,0,0,0.15)';
      case 'strong':
        return '0 8px 32px rgba(0,0,0,0.25)';
      case 'long-shadow':
        return `8px 8px 0 ${theme.accent}80, 16px 16px 0 ${theme.accent}60, 24px 24px 0 ${theme.accent}40`;
      default:
        return borderStyle?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)';
    }
  }, [borderStyle, theme.accent]);

  // 테두리 색상 계산
  const borderColor = useMemo(() => {
    if (borderStyle?.id === 'bold-frame') {return theme.accent;}
    if (borderStyle?.id === 'frame') {return `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`;}
    return '#E5E7EB';
  }, [borderStyle?.id, theme.accent, theme.primary, theme.secondary]);

  return (
    <div 
      className="slide-renderer-container"
      style={{
        width: `${actualWidth}px`,
        height: `${actualHeight}px`,
        transform: borderStyle?.id === 'postit' || disableTransformScale ? 'none' : `scale(${scale})`,
        transformOrigin: 'center center',
        position: 'relative',
        border: (() => {
          // 즉석 사진 스타일일 때는 슬라이드 전체에 테두리 적용하지 않음
          if (borderStyle?.id === 'polaroid') {
            return 'none';
          }
          return borderStyle ? `${borderStyle.borderWidth}px ${borderStyle.borderStyle} ${borderColor}` : '1px solid #E5E7EB';
        })(),
        borderRadius: borderStyle?.id === 'polaroid' ? '0' : (borderStyle ? `${borderStyle.borderRadius}px` : '8px'),
        boxShadow: shadowStyle,
        // 포스트잇 스타일일 때는 customStyle 제외
        ...(borderStyle?.id !== 'postit' ? borderStyle?.customStyle : {}),
      }}
    >
      {/* 핀 스타일일 때 핀 추가 */}
      {borderStyle?.id === 'pin' && (
        <div 
          className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50"
          style={{
            width: '24px',
            height: '24px',
            background: `radial-gradient(circle, ${theme.accent}, ${theme.primary})`,
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          <div 
            className="absolute inset-2 bg-white rounded-full"
            style={{
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)',
            }}
          />
        </div>
      )}
      
      {/* 배경 레이어 */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: borderStyle ? `${borderStyle.borderRadius}px` : '8px',
        }}
      >
        <SlideBackground slide={slide} theme={theme} aspectRatio={aspectRatio} />
      </div>
      
      {/* 패턴 오버레이 레이어 */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: borderStyle ? `${borderStyle.borderRadius}px` : '8px',
        }}
      >
        <SlidePatternOverlay slide={slide} />
      </div>
      
      {/* 통합된 요소 레이어 (제목/내용 포함) */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: borderStyle ? `${borderStyle.borderRadius}px` : '8px',
        }}
      >
        <SlideElements
          slide={slide}
          theme={theme}
          themeFont={themeFont}
          borderStyle={borderStyle}
          isEditable={isEditable}
          selectedElementId={selectedElementId}
          selectedElementIds={selectedElementIds}
          editingElementId={editingElementId}
          onElementSelect={onElementSelect}
          onElementUpdate={onElementUpdate}
          onMultiElementUpdate={onMultiElementUpdate}
          onElementDelete={onElementDelete}
          onEditingElementChange={onEditingElementChange}
          onLayoutChange={onLayoutChange}
          containerWidth={actualWidth}
          containerHeight={actualHeight}
          onSlideUpdate={onSlideUpdate}
          isDarkMode={isDarkMode}
          scale={scale}
          disableTransformScale={disableTransformScale}
        />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  return (
    prevProps.slide.id === nextProps.slide.id &&
    prevProps.slide.htmlContent === nextProps.slide.htmlContent &&
    prevProps.slide.elements === nextProps.slide.elements &&
    prevProps.slide.backgroundColor === nextProps.slide.backgroundColor &&
    prevProps.slide.backgroundImage === nextProps.slide.backgroundImage &&
    prevProps.theme === nextProps.theme &&
    prevProps.aspectRatio === nextProps.aspectRatio &&
    prevProps.language === nextProps.language &&
    prevProps.themeFont === nextProps.themeFont &&
    prevProps.borderStyle === nextProps.borderStyle &&
    prevProps.isEditable === nextProps.isEditable &&
    prevProps.scale === nextProps.scale &&
    prevProps.selectedElementId === nextProps.selectedElementId &&
    prevProps.selectedElementIds === nextProps.selectedElementIds &&
    prevProps.editingElementId === nextProps.editingElementId &&
    prevProps.isDarkMode === nextProps.isDarkMode
  );
});

SlideRenderer.displayName = 'SlideRenderer';